export const MODULES = [
  { id: 'dashboard', label: 'Dashboard', path: '/' },
  { id: 'crm', label: 'CRM', path: '/crm' },
  { id: 'mensajes', label: 'Mensajes', path: '/mensajes' },
  { id: 'calendario', label: 'Calendario', path: '/calendario' },
  { id: 'kanban', label: 'Proyectos', path: '/kanban' },
  { id: 'contratos', label: 'Contratos', path: '/contratos' },
  { id: 'cotizaciones', label: 'Cotizaciones', path: '/cotizaciones' },
  { id: 'finanzas', label: 'Finanzas', path: '/finanzas' },
  { id: 'marketing', label: 'Marketing', path: '/marketing' },
  { id: 'postventa', label: 'Post-venta', path: '/postventa' },
  { id: 'inteligencia', label: 'Inteligencia', path: '/inteligencia' },
  { id: 'notificaciones', label: 'Notificaciones', path: '/notificaciones' },
  { id: 'novedades', label: 'Novedades', path: '/novedades' },
  { id: 'salud', label: 'Salud Operacional', path: '/salud' },
  { id: 'admin', label: 'Administración', path: '/admin' },
  { id: 'portal', label: 'Portal cliente', path: '/portal' },
];

export const ROLE_PRESETS = {
  admin: {
    label: 'Administrador',
    desc: 'Control total: operación, configuración, equipo y finanzas.',
    modules: MODULES.map(m => m.id),
  },
  crm: {
    label: 'CRM / Dirección comercial',
    desc: 'Control operativo completo, incluyendo CRM, proyectos, cotizaciones, marketing y finanzas de lectura/gestión según flujo.',
    modules: ['dashboard','crm','mensajes','calendario','kanban','contratos','cotizaciones','finanzas','marketing','postventa','inteligencia','notificaciones','portal'],
  },
  creativo: {
    label: 'Creativo / Diseñador',
    desc: 'Acceso a proyectos y a la edición de novedades. Sin dashboard, calendario, finanzas ni inteligencia.',
    modules: ['kanban','novedades','notificaciones'],
  },
  finanzas: {
    label: 'Finanzas',
    desc: 'Solo dashboard financiero, finanzas y reportes básicos.',
    modules: ['dashboard','finanzas','inteligencia','notificaciones'],
  },
  cliente: {
    label: 'Cliente',
    desc: 'Solo portal del cliente vinculado a su email.',
    modules: ['client_portal'],
  },
  profile_error: {
    label: 'Perfil no disponible',
    desc: 'Sesion autenticada sin perfil operativo cargado.',
    modules: [],
  },
  custom: {
    label: 'Personalizado',
    desc: 'El administrador decide exactamente qué módulos puede ver.',
    modules: [],
  },
};

export const ROUTE_MODULE = MODULES.reduce((acc, module) => ({ ...acc, [module.path]: module.id }), {});

/**
 * Rutas que NO son un modulo propio y heredan la autoridad de otro.
 *
 * Existe por una sola razon: `canAccessRoute` resuelve una ruta desconocida
 * como `dashboard`, que es el modulo MAS accesible del sistema. Una pantalla
 * nueva sin entrada aqui no queda cerrada por defecto: queda ABIERTA a
 * cualquiera que tenga Dashboard, en silencio y sin que nada falle.
 *
 * `/cotizaciones-v2` FUE el canario del rediseno y hoy solo redirige a
 * `/cotizaciones`, que es donde vive la pantalla. La entrada se conserva a
 * proposito: si alguien volviera a montar contenido en esa ruta, sin alias
 * quedaria resuelta como `dashboard` —el modulo mas accesible— y abierta a
 * cualquiera en silencio. Cuesta una linea y cierra esa puerta.
 */
export const ROUTE_MODULE_ALIAS = Object.freeze({
  '/cotizaciones-v2': 'cotizaciones',
});

const ROLE_REQUIRED_MODULES = {
  crm: ['inteligencia', 'notificaciones'],
  creativo: ['notificaciones'],
  finanzas: ['inteligencia', 'notificaciones'],
};

export function normalizePermissions(role = 'creativo', permissions = null) {
  const preset = ROLE_PRESETS[role] || ROLE_PRESETS.creativo;
  const modules = role === 'creativo'
    ? preset.modules
    : (Array.isArray(permissions?.modules)
      ? permissions.modules
      : preset.modules);
  const required = ROLE_REQUIRED_MODULES[role] || [];
  return {
    modules: Array.from(new Set([...modules, ...required])),
    brands: Array.isArray(permissions?.brands) ? permissions.brands : ['feria', 'bl'],
  };
}

export function canAccessModule(user, moduleId) {
  if (!user || !moduleId) return false;
  if (user.role === 'admin' || user.perms === 'admin') return true;
  if (user.role === 'cliente' || user.perms === 'cliente') return moduleId === 'client_portal';
  const normalized = normalizePermissions(user.role || user.perms, user.permissions);
  return normalized.modules.includes(moduleId);
}

// Editor de Novedades del portal: gate ESPEJO del gate del BD (migración 170,
// has_role('admin','crm') OR has_module('novedades')). Acceso por MÓDULO (custom con
// 'novedades' en su JSON, p.ej. Anita/Fabi) O por ROL (admin/crm, que acceden aunque NO
// tengan el módulo en su JSON). Vive FUERA de canAccessModule a propósito: ese predicado
// responde "¿tiene el módulo?" y no debe mentir para un rol. Lo consumen el guard de ruta
// (canAccessRoute) y el filtro del sidebar (Layout).
export function canAccessNovedades(user) {
  if (!user) return false;
  const role = user.role || user.perms;
  return canAccessModule(user, 'novedades') || role === 'admin' || role === 'crm';
}

// isDesignerView(user): ¿el usuario ve el Kanban en modo DISEÑADOR (interfaz RESTRINGIDA —
// sin precios, acta, conteos de descarga, enlace de Drive, publicar-al-cliente ni asignar)?
// Diseñador = rol creativo legacy, O rol custom con el módulo 'kanban' y SIN módulos
// administrativos (Anita/Fabi). admin/crm/finanzas/cliente => false: ven su interfaz completa.
//
// ASIMETRÍA DELIBERADA con is_project_staff() de la BD — NO son gemelos, no se deben "unificar":
//   · is_project_staff() = creativo OR has_module('kanban')  -> responde "¿ACCEDE a estos datos?".
//     Un rol crm con el módulo kanban da TRUE (la RLS lo deja leer los datos del proyecto).
//   · isDesignerView EXCLUYE crm/finanzas                    -> responde "¿ve la interfaz RESTRINGIDA?".
//     Ese mismo usuario crm da FALSE: ve la interfaz de gestión completa, no la del diseñador.
// Preguntas distintas. Si alguien los colapsa en un solo helper, rompe accesos de admin/crm en silencio.
export function isDesignerView(user) {
  if (!user) return false;
  const role = String(user.role || user.perms || '').toLowerCase();
  if (['admin', 'crm', 'finanzas', 'cliente', 'profile_error'].includes(role)) return false;
  if (['creativo', 'designer', 'disenador', 'diseñador'].includes(role)) return true;
  // custom con proyectos (kanban) y sin módulos administrativos = diseñador
  return canAccessModule(user, 'kanban') && !canAccessModule(user, 'crm') && !canAccessModule(user, 'finanzas');
}

// canManageProjectWork(user): ¿puede PRODUCIR trabajo del proyecto (subir borradores de assets
// finales)? admin (todo) o cualquier diseñador. Publicar y archivar son gates APARTE (solo admin)
// y este helper no los toca.
//
// USA isDesignerView, pero responde OTRA pregunta — es implementación, NO equivalencia:
//   · isDesignerView(user)        -> "¿ve la interfaz RESTRINGIDA?"
//   · canManageProjectWork(user)  -> "¿puede PRODUCIR trabajo del proyecto?"
// Que uno llame al otro es un detalle de implementación. Si alguien cambia isDesignerView,
// ARRASTRA las capacidades sin quererlo: tenelo presente antes de tocar cualquiera de los dos.
export function canManageProjectWork(user) {
  if (!user) return false;
  const role = String(user.role || user.perms || '').toLowerCase();
  return role === 'admin' || isDesignerView(user);
}

export function canAccessRoute(user, path) {
  const moduleId = ROUTE_MODULE[path] || ROUTE_MODULE_ALIAS[path] || 'dashboard';
  if (moduleId === 'novedades') return canAccessNovedades(user);
  return canAccessModule(user, moduleId);
}

export function getDefaultRouteForUser(user) {
  if (!user) return '/login';
  if (user.profileLoadError || user.role === 'profile_error' || user.perms === 'profile_error') return '/';
  if (user.role === 'cliente' || user.perms === 'cliente' || user.isClient) return '/';
  if (user.role === 'admin' || user.perms === 'admin') return '/';
  const normalized = normalizePermissions(user.role || user.perms, user.permissions);
  const routeOrder = ['dashboard','crm','mensajes','calendario','kanban','contratos','cotizaciones','finanzas','marketing','postventa','inteligencia','notificaciones','novedades','admin','portal'];
  const firstModule = routeOrder.find(moduleId => normalized.modules.includes(moduleId)) || 'kanban';
  const found = MODULES.find(m => m.id === firstModule);
  return found?.path || '/kanban';
}


// ╔═ MAPA MODULO -> OWNER DE NOTIFICACIONES — PUENTE DE TRANSICION ═══════════════════╗
// ║                                                                                   ║
// ║ ⚠️ ESTO ES UN PUENTE, NO LA ARQUITECTURA.                                         ║
// ║                                                                                   ║
// ║ El problema de fondo es que `team.role` guarda texto descriptivo mientras         ║
// ║ `profiles.role` guarda el rol efectivo, y los dos cuentan historias distintas.    ║
// ║ Los dos disenadores reales tienen `profiles.role = 'custom'`, asi que un          ║
// ║ `owner: 'creativo'` no les matcheaba por ninguna via y el filtro "Asignado a mi"  ║
// ║ les quedaba vacio.                                                                ║
// ║                                                                                   ║
// ║ LA SOLUCION DE FONDO (frente propio, «(B)»): derivar los `owner` estandar de los  ║
// ║ MODULOS en la EMISION, no solo en la recepcion, y decidir que pasa con el         ║
// ║ historico. Cuando eso se haga, **REEMPLAZA ESTE MAPA** — que por eso vive aca,    ║
// ║ exportado y en un solo lugar, y no incrustado dentro de `matchesOwner`.           ║
// ║ Se cambia un archivo, no se persigue por el repo.                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════════════╝
export const OWNERS_POR_MODULO = {
  kanban: 'creativo',
  crm: 'crm',
  cotizaciones: 'crm',
  contratos: 'crm',
  mensajes: 'crm',
  portal: 'crm',
  postventa: 'crm',
  marketing: 'crm',
  calendario: 'crm',
  finanzas: 'finanzas',
  admin: 'admin',
  // SIN OWNER a proposito: dashboard, inteligencia, notificaciones, novedades y salud
  // son modulos de LECTURA. Tener acceso a un tablero no es ser responsable de nada.
  //
  // 🕳️ HUECO EXPLICADO — `revisor` NO SE DERIVA DE MODULOS PORQUE NO ES UN MODULO.
  // Ser revisor es un rol DENTRO DE UN PROYECTO (`proyectos.assigned_reviewer_id`), no
  // un permiso de aplicacion. Mapearlo desde `kanban` le daria a todo el que produce las
  // notificaciones de quien revisa, que es lo contrario de lo que se quiere. Se resuelve
  // por asignacion y va con (B).
  //   > Un hueco explicado es mejor que un mapeo inventado.
};

// Owners que le corresponden a un usuario, por sus MODULOS. Devuelve un CONJUNTO:
// quien tiene varios modulos recibe las notificaciones de TODOS, nunca elige uno.
// Nadie se queda sin enterarse por tener mas permisos que otro.
export function ownersDelUsuario(user) {
  if (!user) return new Set();
  const rol = String(user.role || user.perms || '').toLowerCase();
  if (rol === 'admin') return new Set(Object.values(OWNERS_POR_MODULO));
  if (rol === 'cliente') return new Set();

  const owners = new Set();
  if (OWNERS_POR_MODULO[rol]) owners.add(OWNERS_POR_MODULO[rol]);  // rol estandar: sigue valiendo

  const modulos = normalizePermissions(user.role || user.perms, user.permissions).modules || [];
  for (const modulo of modulos) {
    const owner = OWNERS_POR_MODULO[modulo];
    if (owner) owners.add(owner);
  }

  // NADA EN SILENCIO. Un perfil que no matchea ningun modulo conocido no recibe nada, y
  // ese es EXACTAMENTE el caso que dejo 525 notificaciones sin destinatario valido. Que
  // grite en consola en vez de devolver un conjunto vacio sin explicacion.
  if (!owners.size) {
    console.warn('[permissions.ownersDelUsuario] Perfil sin owner derivable: no va a ver nada en "Asignado a mi".', {
      user_id: user.id || user.user_id || null,
      role: rol || '(vacio)',
      modulos,
    });
  }
  return owners;
}

export function initialsFromName(name = '', email = '') {
  const cleaned = String(name || '').trim();
  if (cleaned) return cleaned.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return String(email || 'US').slice(0, 2).toUpperCase();
}
